import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * BeforeInstallPromptEvent type for PWA install prompt
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * Service Worker registration state
 */
export interface PWAState {
  isInstallable: boolean;
  isInstalled: boolean;
  isOnline: boolean;
  isUpdateAvailable: boolean;
  isUpdating: boolean;
  registration: ServiceWorkerRegistration | null;
}

/**
 * PWA hook return type
 */
export interface UsePWAReturn extends PWAState {
  installApp: () => Promise<boolean>;
  updateApp: () => Promise<void>;
  dismissUpdate: () => void;
  checkForUpdates: () => Promise<void>;
}

// Storage keys
const INSTALL_DISMISSED_KEY = 'pwa-install-dismissed';
const UPDATE_DISMISSED_KEY = 'pwa-update-dismissed';
const ALLOW_INSTALL_PROMPT =
  import.meta.env.DEV || import.meta.env.VITE_PWA_INSTALL_PROMPT === 'true';

/**
 * Check if running as installed PWA
 */
function checkIfInstalled(): boolean {
  if (typeof window === 'undefined') return false;

  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true ||
    document.referrer.includes('android-app://')
  );
}

/**
 * Hook to manage PWA functionality including:
 * - Service worker registration
 * - Install prompt handling
 * - Update notifications
 * - Online/offline status
 */
export function usePWA(): UsePWAReturn {
  const [state, setState] = useState<PWAState>({
    isInstallable: false,
    isInstalled: checkIfInstalled(),
    isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
    isUpdateAvailable: false,
    isUpdating: false,
    registration: null,
  });

  const deferredPromptRef = useRef<BeforeInstallPromptEvent | null>(null);
  const waitingWorkerRef = useRef<ServiceWorker | null>(null);

  // Clean up stale service workers from previous builds (e.g. sw-v3 after upgrading to sw-v4)
  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }
    const CURRENT_SW = 'sw-v4.js';
    navigator.serviceWorker.getRegistrations().then(registrations => {
      registrations.forEach(registration => {
        const scriptURL = registration.active?.scriptURL || registration.installing?.scriptURL || registration.waiting?.scriptURL || '';
        if (scriptURL && !scriptURL.endsWith(CURRENT_SW)) {
          registration.unregister();
        }
      });
    }).catch(() => { /* ignore */ });
  }, []);

  // Register service worker
  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }

    let updateCheckInterval: ReturnType<typeof setInterval> | null = null;

    const registerSW = async () => {
      try {
        // Import virtual PWA registration module (generated by vite-plugin-pwa)
        const { registerSW } = await import('virtual:pwa-register');

        const updateSW = registerSW({
          immediate: true,
          onRegisteredSW(swUrl, registration) {
            if (registration) {
              setState(prev => ({ ...prev, registration }));

              // Check for updates periodically (every 60 minutes)
              updateCheckInterval = setInterval(() => {
                registration.update().catch(console.error);
              }, 60 * 60 * 1000);
            }
          },
          onNeedRefresh() {
            setState(prev => ({ ...prev, isUpdateAvailable: true }));
          },
          onOfflineReady() {
            console.log('PWA: App ready for offline use');
          },
          onRegisterError(error) {
            console.error('PWA: Service worker registration failed:', error);
          },
        });

        // Store the update function for later use
        (window as any).__updateSW = updateSW;
      } catch (error) {
        // Virtual module not available (development or build issue)
        console.log('PWA: Registration module not available');
      }
    };

    registerSW();

    return () => {
      if (updateCheckInterval) clearInterval(updateCheckInterval);
    };
  }, []);

  // Handle install prompt
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!ALLOW_INSTALL_PROMPT) {
      return;
    }

    // Check if user has dismissed recently
    const dismissedAt = localStorage.getItem(INSTALL_DISMISSED_KEY);
    if (dismissedAt) {
      const dismissedDate = new Date(dismissedAt);
      const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        return; // Don't show install prompt for 7 days after dismissal
      }
    }

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      deferredPromptRef.current = e as BeforeInstallPromptEvent;
      setState(prev => ({ ...prev, isInstallable: true }));
    };

    const handleAppInstalled = () => {
      deferredPromptRef.current = null;
      localStorage.removeItem(INSTALL_DISMISSED_KEY);
      setState(prev => ({
        ...prev,
        isInstallable: false,
        isInstalled: true,
      }));
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Handle online/offline status
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleOnline = () => {
      setState(prev => ({ ...prev, isOnline: true }));
    };

    const handleOffline = () => {
      setState(prev => ({ ...prev, isOnline: false }));
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Handle display mode changes (detect if installed while page is open)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(display-mode: standalone)');

    const handleChange = (e: MediaQueryListEvent) => {
      setState(prev => ({ ...prev, isInstalled: e.matches }));
    };

    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  /**
   * Trigger the install prompt
   */
  const installApp = useCallback(async (): Promise<boolean> => {
    const prompt = deferredPromptRef.current;
    if (!prompt) {
      console.log('PWA: No install prompt available');
      return false;
    }

    try {
      await prompt.prompt();
      const { outcome } = await prompt.userChoice;

      deferredPromptRef.current = null;
      setState(prev => ({ ...prev, isInstallable: false }));

      if (outcome === 'accepted') {
        console.log('PWA: User accepted install prompt');
        return true;
      } else {
        console.log('PWA: User dismissed install prompt');
        localStorage.setItem(INSTALL_DISMISSED_KEY, new Date().toISOString());
        return false;
      }
    } catch (error) {
      console.error('PWA: Error showing install prompt:', error);
      return false;
    }
  }, []);

  /**
   * Apply the waiting service worker update
   */
  const updateApp = useCallback(async (): Promise<void> => {
    setState(prev => ({ ...prev, isUpdating: true }));

    try {
      const updateFn = (window as any).__updateSW;
      if (updateFn) {
        await updateFn(true); // Force reload after update
      } else if (waitingWorkerRef.current) {
        waitingWorkerRef.current.postMessage({ type: 'SKIP_WAITING' });
        window.location.reload();
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('PWA: Error updating app:', error);
      setState(prev => ({ ...prev, isUpdating: false }));
    }
  }, []);

  /**
   * Dismiss the update notification
   */
  const dismissUpdate = useCallback(() => {
    setState(prev => ({ ...prev, isUpdateAvailable: false }));
    localStorage.setItem(UPDATE_DISMISSED_KEY, new Date().toISOString());
  }, []);

  /**
   * Manually check for updates
   */
  const checkForUpdates = useCallback(async (): Promise<void> => {
    if (state.registration) {
      try {
        await state.registration.update();
      } catch (error) {
        console.error('PWA: Error checking for updates:', error);
      }
    }
  }, [state.registration]);

  return {
    ...state,
    installApp,
    updateApp,
    dismissUpdate,
    checkForUpdates,
  };
}

/**
 * Simple hook to just check installation status
 */
export function useIsInstalled(): boolean {
  const [isInstalled, setIsInstalled] = useState(checkIfInstalled);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(display-mode: standalone)');

    const handleChange = (e: MediaQueryListEvent) => {
      setIsInstalled(e.matches);
    };

    window.addEventListener('appinstalled', () => setIsInstalled(true));
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  return isInstalled;
}

export default usePWA;
